'use client';

import { useState, useCallback } from 'react';
import { ShieldCheck, ShieldOff, UserCog } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDirectorCoaches,
  usePromoteToDirector,
  useRevokeDirectorAccess,
} from '@/hooks/use-director-data';
import type { CoachSummary } from '@/hooks/use-director-data';

export default function DirectorCoachesPage() {
  const [confirmGrantCoach, setConfirmGrantCoach] = useState<CoachSummary | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);

  const { data: coachesData, isLoading: coachesLoading } = useDirectorCoaches();
  const promoteToDirector = usePromoteToDirector();
  const revokeDirectorAccess = useRevokeDirectorAccess();

  const handleGrantAccess = useCallback(async () => {
    if (!confirmGrantCoach) return;
    setGrantError(null);
    try {
      await promoteToDirector.mutateAsync(confirmGrantCoach.id);
      setConfirmGrantCoach(null);
    } catch (err) {
      setGrantError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }, [confirmGrantCoach, promoteToDirector]);

  return (
    <div className="space-y-6">
      <PageHeader title="Coaches" subtitle="Manage coaching staff and access levels" />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" />
            All Coaches
          </CardTitle>
        </CardHeader>
        <CardContent>
          {coachesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : coachesData?.coaches.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No coaches in your club yet.
            </p>
          ) : (
            <div className="space-y-2">
              {coachesData?.coaches.map((coach) => (
                <div
                  key={coach.id}
                  className="rounded-lg border border-border p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white font-bold shrink-0">
                        {coach.email[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground">
                            {coach.email.split('@')[0]}
                          </p>
                          {coach.hasDirectorAccess && (
                            <Badge variant="secondary" className="text-xs">
                              Director
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{coach.email}</p>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {coach.hasDirectorAccess ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeDirectorAccess.mutateAsync(coach.id)}
                          disabled={revokeDirectorAccess.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs h-7"
                        >
                          <ShieldOff className="w-3 h-3 mr-1" />
                          Revoke Director Access
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmGrantCoach(coach)}
                          className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 text-xs h-7"
                        >
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Grant Director Access
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Grant Director Access */}
      <Dialog open={!!confirmGrantCoach} onOpenChange={(open) => { if (!open) { setConfirmGrantCoach(null); setGrantError(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Director Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to grant director access to{' '}
              <span className="font-semibold text-foreground">
                {confirmGrantCoach?.email.split('@')[0]}
              </span>
              ? They will be able to manage all players, sessions, invoices, and other coaches.
            </p>
            {grantError && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{grantError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setConfirmGrantCoach(null); setGrantError(null); }}>
                Cancel
              </Button>
              <Button
                onClick={handleGrantAccess}
                disabled={promoteToDirector.isPending}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                {promoteToDirector.isPending ? 'Granting...' : 'Grant Access'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
